import * as React from "react";
import type { SchemaNode } from "@/lib/schema/types";

const { useMemo } = React;

interface SchemaGraphProps {
  /** Liste de nodes JSON-LD à injecter dans un seul @graph */
  nodes: (SchemaNode | null | undefined | false)[];
}

/**
 * SchemaGraph — injecte un unique <script type="application/ld+json">
 * contenant `@context` + `@graph` regroupant tous les nodes d'une page.
 *
 * Avantages vs plusieurs scripts séparés :
 *  - 1 seul script à parser pour Google
 *  - références cross-entités via @id propres
 *  - moins de bruit dans le HTML
 *  - bonne pratique recommandée 2024+
 *
 * Les nodes falsy (null/undefined/false) sont ignorés — pratique pour
 * conditionner des schemas.
 *
 * Performance : la sérialisation JSON est mémoïsée sur la référence du tableau
 * `nodes` pour éviter de relancer un `JSON.stringify` du graphe complet à chaque
 * render parent. Les pages doivent stabiliser `nodes` via `useMemo`.
 */
export function SchemaGraph({ nodes }: SchemaGraphProps) {
  const html = useMemo(() => {
    const filtered = nodes.filter(Boolean) as SchemaNode[];
    if (filtered.length === 0) return null;
    return JSON.stringify({
      "@context": "https://schema.org",
      "@graph": filtered,
    });
  }, [nodes]);

  if (!html) return null;

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
